import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { RoleService } from '@karve.it/core';
import { User } from '@karve.it/interfaces/auth';
import { ZoneDir as commonZoneDir } from '@karve.it/interfaces/common.gql';
import { ListRolesOutput, RoleInfo } from '@karve.it/interfaces/roles';
import { Apollo, QueryRef } from 'apollo-angular';

import { fromEvent } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { PeopleCustomersCountGQL, PeopleCustomersGQL, UserPageCountGQL, UserPageUsersGQL, ZoneDir } from '../../../generated/graphql.generated';
import { CustomInputsModule } from '../../custom-inputs/custom-inputs.module';
import { FreyaCommonModule } from '../../freya-common/freya-common.module';
import { pagination } from '../../global.constants';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaHelperService } from '../../services/freya-helper.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { MutateUserComponent } from '../../shared/mutate-user/mutate-user.component';
import { QUICK_INVITE_MUTATION } from '../../shared/mutate-user/mutate-user.queries';
import { EMAIL_VALIDATION_PATTERN } from '../../shared/pattern.validator';
import { SharedModule } from '../../shared/shared.module';
import { WatchQueryHelper } from '../../utilities/watchQueryHelper';

import { PeopleSearchService } from './people-search.service';
import { PeopleTabConfig } from './people-tab-config';

@Component({
    selector: 'app-people-list',
    templateUrl: './people-list.component.html',
    styleUrls: ['./people-list.component.scss'],
    standalone: true,
    imports: [
        FreyaCommonModule,
        SharedModule,
        CustomInputsModule,
    ],
})
export class PeopleListComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() config!: PeopleTabConfig;

    @ViewChild('userSearchInput', { static: true }) userSearchInput!: ElementRef;
    @ViewChild('mutate') mutateRef: MutateUserComponent;

    subs = new SubSink();

    users: any[] = [];

    usersQueryRef!: QueryRef<any, any>;
    usersQH: WatchQueryHelper = {
        limit: pagination.defaultNumberOfItems,
        skip: 0,
        loading: true,
        search: '',
        hasMore: true,
        mergeNextResults: false,
        total: undefined,
    };

    userCountLoading = false;
    userPlaceholderCount = Array.from({ length: this.usersQH.limit });

    rolesQueryRef: QueryRef<ListRolesOutput>;
    availableRoles: RoleInfo[];
    filteredRoles: RoleInfo[];
    selectedRoles: RoleInfo[];

    pagination = pagination;

    inviteDialogVisible = false;
    inviteName = '';
    inviteContact = '';
    inviteSelectedRoles: RoleInfo[] = [];
    inviteLoading = false;

    constructor(
        private userPageUsersGQL: UserPageUsersGQL,
        private peopleCustomersGQL: PeopleCustomersGQL,
        private dhs: DetailsHelperService,
        private roleService: RoleService,
        private router: Router,
        private freyaHelper: FreyaHelperService,
        private notify: FreyaNotificationsService,
        private usersPageCountGQL: UserPageCountGQL,
        private peopleCustomersCountGQL: PeopleCustomersCountGQL,
        private elementRef: ElementRef,
        private apollo: Apollo,
        private searchService: PeopleSearchService,
    ) { }

    ngOnInit(): void {
        this.usersQH.search = this.searchService.search;

        this.subs.sink = this.dhs.getObjectUpdates('User').subscribe(() => {
            if (this.usersQueryRef) {
                this.usersQueryRef.refetch();
            }
        });

        this.subs.sink = fromEvent(this.userSearchInput.nativeElement, 'keyup').pipe(
            map((event: any) => event.target.value),
            debounceTime(750),
        ).subscribe((text: string) => {
            this.searchService.search = text;
            this.usersQH.search = text;
            this.usersQH.mergeNextResults = false;
            this.usersQH.skip = 0;
            this.usersQH.total = undefined;
            this.searchForUsers();
        });
        this.retrieveUsers();
        this.fetchRoles();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        this.users = [];
    }

    ngAfterViewInit(): void {
        if (this.usersQH.search) {
            this.userSearchInput.nativeElement.value = this.usersQH.search;
        }
    }

    retrieveUsers() {
        const gql = this.config.showJobs ? this.peopleCustomersGQL : this.userPageUsersGQL;
        this.usersQueryRef = gql.watch(
            {
                limit: this.usersQH.limit,
                filter: this.buildFilter(),
            }, { notifyOnNetworkStatusChange: true });

        this.subs.sink = this.usersQueryRef.valueChanges.subscribe((res: any) => {
            this.usersQH.loading = res.loading;
            if (res.loading) { return; }
            if (this.usersQH.mergeNextResults) {
                this.users = [...this.users, ...res.data.usersv2.users];
            } else {
                this.users = res.data.usersv2.users;
            }
            this.usersQH.hasMore = res.data.usersv2.users.length === this.usersQH.limit;
        });
    }

    buildFilter(): any {
        const filter: any = {
            zoneDir: ZoneDir.Lte,
        };
        if (this.config.roleAttributes) {
            filter.roleHasAttributes = this.config.roleAttributes;
        }
        if (this.selectedRoles && this.selectedRoles.length) {
            filter.roles = this.selectedRoles.map((r) => r.id);
        }
        if (this.usersQH.search.trim()) {
            filter.search = this.usersQH.search.trim();
        }
        return filter;
    }

    retrieveMoreUsers() {
        if (this.usersQH.loading || !this.usersQH.hasMore) { return; }
        this.usersQH.skip = this.usersQH.skip + this.usersQH.limit;
        this.usersQH.mergeNextResults = true;
        this.searchForUsers();
    }

    searchForUsers() {
        this.usersQH.loading = true;
        if (this.usersQueryRef) {
            this.usersQueryRef.refetch({
                filter: this.buildFilter(),
                limit: this.usersQH.limit,
                skip: this.usersQH.skip,
            });
        } else {
            this.retrieveUsers();
        }
    }

    selectUser(user: User) {
        this.dhs.detailsItem.next({ type: 'users', item: { id: user.id } });
    }

    openCreateUser() {
        this.mutateRef.mutateType = 'create';
        this.mutateRef.openDialog();
    }

    clearSearch() {
        this.usersQH.search = '';
        this.searchService.search = '';
        this.usersQH.mergeNextResults = false;
        this.usersQH.skip = 0;
        this.usersQH.total = undefined;
        this.searchForUsers();
    }

    searchUsersByRole() {
        this.usersQH.mergeNextResults = false;
        this.usersQH.skip = 0;
        this.usersQH.total = undefined;
        this.searchForUsers();
    }

    getUserCount() {
        const countGql = this.config.showJobs ? this.peopleCustomersCountGQL : this.usersPageCountGQL;
        countGql.fetch({
            filter: this.buildFilter(),
        }, { notifyOnNetworkStatusChange: true }).subscribe((res: any) => {
            this.userCountLoading = res.loading;
            if (res.loading) { return; }
            this.usersQH.total = res.data.usersv2.total;
        });
    }

    fetchRoles() {
        this.rolesQueryRef = this.roleService.watchRoles({ zoneDir: commonZoneDir.lte, limit: -1 });
        this.subs.sink = this.rolesQueryRef.valueChanges.subscribe((res) => {
            if (res.networkStatus === 7) {
                this.availableRoles = res.data.roles;
                if (this.config.roleAttributes && this.availableRoles) {
                    this.filteredRoles = this.availableRoles.filter((r) =>
                        r.attributes && r.attributes.some((attr) => this.config.roleAttributes.includes(attr))
                    );
                } else {
                    this.filteredRoles = this.availableRoles;
                }
            }
        });
    }

    openInviteDialog() {
        this.inviteDialogVisible = true;
        this.inviteName = '';
        this.inviteContact = '';
        this.inviteSelectedRoles = [];
        if (!this.availableRoles) {
            this.fetchRoles();
        }
    }

    closeInviteDialog() {
        this.inviteDialogVisible = false;
    }

    isValidEmail(email: string): boolean {
        return new RegExp(EMAIL_VALIDATION_PATTERN).test(email);
    }

    isValidPhone(contact: string): boolean {
        const digits = contact.replace(/\D/g, '');
        return digits.length >= 7 && digits.length <= 12;
    }

    isValidContact(): boolean {
        return this.isValidEmail(this.inviteContact) || this.isValidPhone(this.inviteContact);
    }

    canSendInvite(): boolean {
        return this.inviteContact?.trim().length > 0 && this.isValidContact();
    }

    sendInvite(inviteAnother = false) {
        if (!this.canSendInvite()) { return; }
        this.inviteLoading = true;

        const contact = this.inviteContact.trim();
        const isEmail = this.isValidEmail(contact);
        const email = isEmail ? contact : undefined;
        const phone = !isEmail ? contact.replace(/\D/g, '') : undefined;
        const roleIds = this.inviteSelectedRoles.map((r) => r.id);

        const nameParts = this.inviteName.trim().split(/\s+/);
        const givenName = nameParts[0] || undefined;
        const familyName = nameParts.slice(1).join(' ') || undefined;

        this.subs.sink = this.apollo.mutate({
            mutation: QUICK_INVITE_MUTATION,
            variables: { email, phone, givenName, familyName, roles: roleIds, sendInvite: isEmail },
        }).subscribe({
            next: (res: any) => {
                this.inviteLoading = false;
                const result = res?.data?.quickInvite;
                if (result?.failedUsers?.length) {
                    this.notify.error(`Failed to invite user: ${result.failedUsers[0].error}`);
                    return;
                }
                this.notify.success('Invite sent successfully');
                if (inviteAnother) {
                    this.inviteName = '';
                    this.inviteContact = '';
                } else {
                    this.closeInviteDialog();
                }
                this.searchForUsers();
            },
            error: (err) => {
                this.inviteLoading = false;
                this.notify.apolloError('Failed to send invite', err);
            },
        });
    }

    openNewOpportunity() {
        this.router.navigate(['/jobs', 'new']);
    }

    openCreateCrew() {
        this.router.navigate(['/schedule', 'dispatch']);
    }

    MAX_ROLE_DISPLAY = 3;
}